import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, Target, DollarSign, Trophy, Users, Calendar, Loader2,
} from "lucide-react";

const STATUS_LABELS = {
  new: "New", visited: "Visited", qualified: "Qualified",
  quote_sent: "Quote Sent", won: "Won", lost: "Lost",
};
const STATUS_COLORS = {
  new: "bg-blue-500/15 text-blue-300",
  visited: "bg-cyan-500/15 text-cyan-300",
  qualified: "bg-amber-500/15 text-amber-300",
  quote_sent: "bg-violet-500/15 text-violet-300",
  won: "bg-emerald-500/15 text-emerald-300",
  lost: "bg-rose-500/15 text-rose-300",
};

export default function SalesReports() {
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sales/reports").then((res) => setR(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !r) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;
  }

  const maxCount = Math.max(1, ...Object.values(r.by_status));

  return (
    <div className="space-y-6" data-testid="sales-reports-page">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <TrendingUp className="w-7 h-7 text-emerald-400" /> Sales Reports
        </h1>
        <p className="text-slate-400 text-sm mt-1">Pipeline KPIs, conversion funnel, and upcoming follow-ups.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total Prospects" value={r.total_prospects} icon={Users} color="blue" testId="kpi-total" />
        <KPI label="Pipeline Value" value={`$${Number(r.pipeline_value).toLocaleString()}`} icon={DollarSign} color="amber" testId="kpi-pipeline" />
        <KPI label="Won Value" value={`$${Number(r.won_value).toLocaleString()}`} icon={Trophy} color="emerald" testId="kpi-won" />
        <KPI label="Win Rate" value={`${r.win_rate}%`} icon={Target} color="cyan" testId="kpi-winrate" />
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5" data-testid="funnel-card">
        <div className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-emerald-400" /> Funnel by Status</div>
        <div className="space-y-2.5">
          {Object.entries(r.by_status).map(([key, count]) => {
            const w = (count / maxCount) * 100;
            return (
              <div key={key} className="flex items-center gap-3">
                <div className="w-24 text-xs text-slate-400 uppercase tracking-wider font-bold">{STATUS_LABELS[key]}</div>
                <div className="flex-1 h-7 bg-slate-900/50 rounded-md overflow-hidden">
                  <div className={`h-full ${STATUS_COLORS[key]} rounded-md flex items-center px-2.5`} style={{ width: `${Math.max(4, w)}%` }}>
                    <span className="text-xs font-medium text-slate-100">{count}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-cyan-400" /> Activities (30d)</div>
          <div className="text-3xl font-bold text-cyan-300" data-testid="activities-30d">{r.activities_30d}</div>
          <p className="text-xs text-slate-500 mt-1">visits, calls, emails, meetings, quotes logged</p>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5" data-testid="upcoming-actions">
          <div className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-400" /> Upcoming Actions</div>
          {(r.upcoming_actions || []).length === 0 ? (
            <p className="text-sm text-slate-500 italic">No follow-ups scheduled.</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {r.upcoming_actions.map((a) => (
                <div key={a.id} className="rounded-md border border-amber-500/20 bg-amber-500/5 p-2 text-xs" data-testid={`upcoming-${a.id}`}>
                  <div className="font-medium text-amber-300">{a.next_action}</div>
                  <div className="text-slate-400 mt-0.5">📅 {a.next_action_date}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, testId }) {
  const c = {
    blue: "border-blue-500/30 text-blue-300",
    amber: "border-amber-500/30 text-amber-300",
    emerald: "border-emerald-500/30 text-emerald-300",
    cyan: "border-cyan-500/30 text-cyan-300",
  }[color];
  return (
    <div data-testid={testId} className={`rounded-xl border ${c} bg-[#0B1120] p-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
